// FLAGS: none
// The expert's sign-up list: scheduled group sessions still without a host, one ISO week per page.
// ?view=calendar shows the same sessions, plus the ones the expert hosts, as a calendar.
import { lazy, Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  useHostSignupMutation,
  useOpenForHostingQuery,
  useWithdrawAsHostMutation,
  useWithdrawHostSignupMutation,
} from "@src/queries/spaces";
import HostSignupsNotice from "@src/views/HostSignups/HostSignupsNotice";
import HostSignupsList from "@src/views/HostSignups/HostSignupsList";
import hostSignupErrorKey from "@src/views/HostSignups/hostSignupError";
import HostSignupsViewSwitch, {
  HostSignupsViewMode,
} from "@src/views/HostSignups/HostSignupsViewSwitch";

// Loaded on first use: the calendar library stays out of the main bundle.
const HostSignupsCalendar = lazy(
  () => import("@src/views/HostSignups/calendar/HostSignupsCalendar"),
);

const HostSignupsView = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const view: HostSignupsViewMode =
    searchParams.get("view") === "calendar" ? "calendar" : "list";
  const [error, setError] = useState<string | null>(null);
  const { data, isLoading, isError } = useOpenForHostingQuery();
  const signUp = useHostSignupMutation();
  const withdraw = useWithdrawHostSignupMutation();
  const withdrawAsHost = useWithdrawAsHostMutation();

  const run = async (
    mutation: typeof signUp | typeof withdraw | typeof withdrawAsHost,
    sessionId: string,
  ): Promise<boolean> => {
    setError(null);
    try {
      await mutation.mutateAsync(sessionId);
      return true;
    } catch (caught) {
      setError(t(hostSignupErrorKey(caught)));
      return false;
    }
  };
  const switchView = (next: HostSignupsViewMode) => {
    setError(null);
    // The filters are shared by both views; week, title and calendar position are not.
    const params = new URLSearchParams();
    ["language", "type", "mine"].forEach((key) => {
      const value = searchParams.get(key);
      if (value) params.set(key, value);
    });
    if (next === "calendar") params.set("view", next);
    setSearchParams(params);
  };

  const busy =
    signUp.isPending || withdraw.isPending || withdrawAsHost.isPending;

  return (
    <div className="p-6">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold text-indigo-800">
          {t("HostSignups.Title")}
        </h1>
        <HostSignupsViewSwitch view={view} onChange={switchView} />
      </div>
      <div className="mt-3">
        <HostSignupsNotice />
      </div>
      <p className="mb-6 text-sm text-slate-700">{t("HostSignups.Intro")}</p>

      {view === "calendar" && data && (
        <Suspense
          fallback={
            <p className="text-sm text-indigo-800">
              {t("HostSignups.Loading")}
            </p>
          }
        >
          <HostSignupsCalendar
            open={data}
            busy={busy}
            error={error}
            onSignUp={(id) => run(signUp, id)}
            onWithdraw={(id) => run(withdraw, id)}
            onWithdrawAsHost={(id) => run(withdrawAsHost, id)}
          />
        </Suspense>
      )}

      {view === "list" && error && (
        <p className="mb-3 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}

      {isLoading && (
        <p className="text-sm text-indigo-800">{t("HostSignups.Loading")}</p>
      )}
      {isError && (
        <p className="text-sm text-red-600" role="alert">
          {t("HostSignups.LoadError")}
        </p>
      )}

      {view === "list" && data && data.length === 0 && (
        <div className="rounded-lg border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-700">
          {t("HostSignups.Empty")}
        </div>
      )}

      {view === "list" && data && data.length > 0 && (
        <HostSignupsList
          sessions={data}
          busy={busy}
          onSignUp={(id) => run(signUp, id)}
          onWithdraw={(id) => run(withdraw, id)}
        />
      )}

      {view === "list" && data && data.length > 0 && (
        <p className="mt-4 text-xs text-slate-700">
          {t("HostSignups.Footnote")}
        </p>
      )}
    </div>
  );
};

export default HostSignupsView;
